/**
 * Turns the print swatches rendered by snippets/product-print-swatches.liquid
 * (with print_selector: true) into an in-place swap of the card they sit in,
 * instead of a navigation to the sibling product.
 *
 * Prints are sibling *products*, not variants, so there is no client-side
 * variant switch to piggyback on. Rather than fetch, the owning section emits
 * the whole shape group once via snippets/print-group-data.liquid and this
 * reads it — a swap is then instant and costs no network at all.
 *
 * Everything the card shows is swapped: images, title, price, badge, both
 * links, the active swatch, and the handle on every quick-add trigger it
 * carries. The card is presentation only — adding to cart happens in the
 * quick-add popup, which fetches by handle — so there is no cart state here
 * that could fall out of step with what is on screen.
 *
 * This is progressive enhancement: the swatches stay real <a href> links, so
 * no-JS, middle-click, cmd-click and a missing/failed payload all fall back to
 * normal navigation.
 */
;(function () {
    const SWATCH = "[data-print-swatch]"
    const GROUP = "[data-print-selector]"
    const CARD = "[data-print-card]"
    const IMAGE = ".js-print-image"
    const TITLE = "[data-print-title]"
    const PRICE = "[data-print-price]"
    const LINK = "[data-print-link]"
    const BADGE = "[data-print-badge]"
    const QUICK_ADD = "[data-quick-add]"

    const DECODE_TIMEOUT_MS = 400 // cap on waiting for the incoming image

    const groupCache = new Map() // group handle -> handle -> entry
    const warnedGroups = new Set() // group handles already reported as missing

    const wait = (ms) =>
        new Promise((resolve) => window.setTimeout(resolve, ms))

    // Group blobs are emitted once per section and shared by every card in it,
    // so parse each one at most once however many cards read it.
    //
    // Misses are deliberately not cached: infinite scroll appends blobs for
    // shapes that weren't on the first page, and a cached null would keep
    // those cards navigating for the rest of the session.
    function getGroup(name) {
        if (groupCache.has(name)) return groupCache.get(name)

        const node = document.querySelector(
            `script[data-print-group="${CSS.escape(name)}"]`
        )
        if (!node) {
            // Falling back to navigation is the designed behaviour, which
            // makes "print_selector: true but the section forgot
            // {% render 'print-group-data' %}" fail completely silently.
            // Say so once per group so it is findable.
            if (!warnedGroups.has(name)) {
                warnedGroups.add(name)
                console.warn(
                    `Print swap: no data for "${name}" — the section rendering these cards needs {% render 'print-group-data', products: ... %}. Swatches will navigate instead.`
                )
            }
            return null
        }

        let byHandle = null
        try {
            byHandle = new Map(
                JSON.parse(node.textContent).map((entry) => [entry.h, entry])
            )
        } catch (error) {
            console.error("Print swap: unreadable group data", name, error)
            return null
        }

        groupCache.set(name, byHandle)
        return byHandle
    }

    function lookup(swatch) {
        const group = swatch.closest(GROUP)
        if (!group) return null

        const byHandle = getGroup(group.dataset.printGroup)
        return byHandle
            ? byHandle.get(swatch.dataset.printHandle) || null
            : null
    }

    // Decode before painting, so the swap doesn't land on an empty box while
    // the new source loads. Same technique as warmImages() in
    // assets/product-fabric-swap.js.
    function warmImage(src) {
        if (!src) return Promise.resolve()

        const image = new Image()
        image.src = src
        return Promise.race([
            image.decode().catch(() => {}),
            wait(DECODE_TIMEOUT_MS),
        ])
    }

    function setActiveSwatch(card, swatch) {
        card.querySelectorAll(SWATCH).forEach((node) =>
            node.setAttribute(
                "aria-current",
                node.dataset.printHandle === swatch.dataset.printHandle
                    ? "true"
                    : "false"
            )
        )
    }

    function applyImages(card, sources) {
        const images = card.querySelectorAll(IMAGE)

        images.forEach((image, index) => {
            const src = sources[index] || sources[0]
            if (!src) return

            // srcset wins over src whenever it's present, so it has to go —
            // the payload carries one resolved size, not a candidate set.
            image.removeAttribute("srcset")
            image.removeAttribute("sizes")
            image.src = src
        })
    }

    function applyEntry(card, entry) {
        card.querySelectorAll(LINK).forEach((link) => (link.href = entry.u))

        const title = card.querySelector(TITLE)
        if (title) title.textContent = entry.t

        const price = card.querySelector(PRICE)
        if (price && entry.p) price.innerHTML = entry.p

        // innerHTML, not textContent: a "Save £39" badge is built with the
        // money filter, and this store's money_format wraps the amount in
        // <span class="money">. textContent would print that markup as
        // literal text, and it also has to survive for any currency
        // converter that hooks .money. Liquid renders the same string
        // unescaped, so both paths agree. The value is theme-generated in
        // snippets/product-badge.liquid — same trust level as entry.p above.
        const badge = card.querySelector(BADGE)
        if (badge) {
            badge.innerHTML = entry.b || ""
            badge.classList.toggle("hidden", !entry.b)
        }

        // Every trigger, not just the first: a card may carry several (over
        // the image, under the swatches, wherever), and any one left on the
        // old handle would open the print the shopper started on.
        card.querySelectorAll(QUICK_ADD).forEach((trigger) => {
            trigger.dataset.quickAdd = entry.h
        })

        applyImages(card, entry.i || [])
    }

    async function onClick(event) {
        if (event.defaultPrevented) return
        if (
            event.button !== 0 ||
            event.metaKey ||
            event.ctrlKey ||
            event.shiftKey ||
            event.altKey
        ) {
            return // let the browser open a new tab / download as asked
        }

        const swatch = event.target?.closest?.(SWATCH)
        if (!swatch) return

        const card = swatch.closest(CARD)
        if (!card) return

        const entry = lookup(swatch)
        if (!entry) return // no payload — let the link navigate

        event.preventDefault()
        if (swatch.getAttribute("aria-current") === "true") return

        // Paint the selection before the await, so the swatch responds
        // instantly even while the incoming image is still decoding.
        setActiveSwatch(card, swatch)

        await warmImage((entry.i || [])[0])
        applyEntry(card, entry)
    }

    // Attached unconditionally rather than behind a "does this page have any
    // swatches" check: the grid replaces its own markup on filter, sort and
    // infinite scroll, so a page can gain its first [data-print-selector] long
    // after load. onClick bails on the first selector miss, which costs
    // nothing on pages that never have any.
    function init() {
        document.addEventListener("click", onClick)
    }

    if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", init)
    } else {
        init()
    }
})()
